import BRANDS, { type BrandName, type BrandZohoTypes } from "@elit-hair/brands";
import { getLocalStorage, useAuth, useLocalStorage } from "@elit-hair/elit-tools";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    useIsMobile,
} from "@elit-hair/elit-ui";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

// Conditionally import dev-only hook
import { AudioDeviceAutoSwitchDialog } from "@/components/call-dock/audio-device-auto-switch-dialog";
import { AudioDeviceRequiredDialog } from "@/components/call-dock/audio-device-required-dialog";
import { CALL_STATUS, HANGUP_COOLDOWN } from "@/constants/call";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { SETTINGS_KEYS } from "@/constants/settings-keys";
import type { AudioDevice } from "@/features/audio";
import { audioStateStore, callManager, deviceManager } from "@/features/telephony";
import { callKeys, createCall, getCall } from "@/hooks/queries/call.queries";
import { useRecord } from "@/hooks/queries/crm.queries";
import { useHandleCallFailure } from "@/hooks/queries/handle-call-failure.queries";
import { useAudioDeviceChangeToasts } from "@/hooks/use-audio-device-change-toasts";
import { useRemoteConfigInput } from "@/pages/settings/utils/use-remote-config-input";
import { useUserStatusContext } from "@/providers/general/user-status.provider";
import { PermissionNames, usePermissions } from "@/providers/hooks/use-permissions";
import { useSingleInstanceContext } from "@/providers/single-instance-provider";
import { useMyPhoneNumbers } from "@/queries/phone-numbers.queries";
import { audioProcessingService } from "@/services/audio-processing-service";
import { soundService } from "@/services/soundService";
import { Call, CallPayload, CallStatus } from "@/types/call.types";
import { IPhoneNumber } from "@/types/phone.types";
import { UserStatus } from "@/types/user-statuses.types";
import {
    logAudioDeviceAutoSwitches,
    logAudioDeviceListDiff,
    logAudioDeviceSelection,
} from "@/utils/audio-device-event-log.utils";
import { type AudioDeviceAutoSwitch, type ResolvedAudioDevices } from "@/utils/audio-device-storage.utils";
import { formatPhoneNumber } from "@/utils/format-phone-number";
import { getBrandFromOrgId } from "@/utils/get-brand-from-org-id";
import { selectOutboundPhoneNumberAutomatically } from "@/utils/phone-numbers/phone-numbers.utils";
import { getStatusBorderColor } from "@/utils/user-status";
import { getVoiceRegistrationToken } from "@/utils/voice-registration.utils";

import { useSimulateDevCall } from "./use-call.dev";
import { useTopbar } from "./use-topbar";

enum USER_STATUS_POPUP_DECISION {
    ACTIVE = "active",
    SILENT = "silent",
    CANCEL = "cancel",
}

enum MULTIPLE_INSTANCES_CALL_DECISION {
    PROCEED = "proceed",
    CANCEL = "cancel",
}

type CallContextType = {
    initialized: boolean;
    setInitialized: (initialized: boolean) => void;

    temporaryCallerPhoneNumber: string | undefined;

    calledPhoneNumber: string;
    setCalledPhoneNumber: (calledPhoneNumber: string) => void;

    callerPhoneNumber: IPhoneNumber | undefined;
    setCallerPhoneNumber: (callerPhoneNumber: IPhoneNumber | undefined) => void;

    callStatus: CallStatus;
    setCallStatus: (callStatus: CallStatus) => void;

    currentRecordInformation: BrandZohoTypes["Leads"] | BrandZohoTypes["Contacts"] | undefined;

    currentCallData: Call | undefined;

    callStartTime: string | null;
    setCallStartTime: (callStartTime: string | null) => void;

    callDuration: number;

    setInputDevice: (deviceId: string) => Promise<void>;
    setOutputDevice: (deviceId: string) => Promise<void>;

    appendToPhoneNumber: (digit: string) => void;
    startCall: (phoneNumber: string, callData?: Omit<Partial<CallPayload>, "To" | "From">) => Promise<void>;
    handleHangupCall: () => void;
    handleSendDigits: (digits: string) => void;
    handleMuteMic: () => void;
    isMuted: boolean;
    audioLevel: number[];

    // DEV-only helpers for UI testing without Twilio.
    simulateCallStart?: (phoneNumber: string, callData?: Omit<Partial<CallPayload>, "To" | "From">) => void;
    simulateCallEnd?: () => void;

    initialize: (token: string) => void;
    isCallActive: (statuses?: CallStatus[]) => boolean;

    selectedBrand: BrandName;
    setSelectedBrand: (selectedBrand: BrandName) => void;

    callButtonCooldown: boolean;
    setCallButtonCooldown: (callButtonCooldown: boolean) => void;

    onCallEnded: (callback: (call: Call) => void) => () => void;
};

const CallContext = createContext<CallContextType | null>(null);

/**
 * Provides call-related state, controls, and UI decision dialogs to descendant components via CallContext.
 *
 * Exposes call lifecycle state, device selection, mute and audio-level metrics, call control handlers (start, hangup, send digits, mute), brand selection, initialization, and callbacks used by the app to manage and observe calls.
 *
 * @param children - React nodes that will consume the CallContext
 * @returns A React provider element that wraps `children` with the call context
 */
export function CallProvider({ children }: { children: React.ReactNode }) {
    const { permissions, openModal: openPermissionsModal } = usePermissions();
    const isMobile = useIsMobile();
    const [initialized, setInitialized] = useState<boolean>(false);
    const [calledPhoneNumber, setCalledPhoneNumber] = useLocalStorage<string>(
        LOCAL_STORAGE_KEYS.CALLED_PHONE_NUMBER.key,
        LOCAL_STORAGE_KEYS.CALLED_PHONE_NUMBER.default
    );
    const [temporaryCallerPhoneNumber, setTemporaryCallerPhoneNumber] = useState<string | undefined>(undefined);
    const [callerPhoneNumber, setCallerPhoneNumber] = useLocalStorage<IPhoneNumber | undefined>(
        LOCAL_STORAGE_KEYS.CALLER_PHONE_NUMBER.key,
        LOCAL_STORAGE_KEYS.CALLER_PHONE_NUMBER.default
    );
    const [callStatus, setCallStatus] = useState<CallStatus>(CALL_STATUS.DISCONNECTED);
    const [currentCallData, setCurrentCallData] = useState<Call | undefined>(undefined);
    const [callStartTime, setCallStartTime] = useState<string | null>(null);
    const [callDuration, setCallDuration] = useState<number>(0);
    const [isMuted, setIsMuted] = useState<boolean>(false);
    const [audioLevel, setAudioLevel] = useState<number[]>([]);
    const muteStatusSyncedRef = useRef<boolean>(false);
    const userHungUpRef = useRef(false);
    const [showIdleConfirm, setShowIdleConfirm] = useState(false);
    const idleDecisionResolverRef = useRef<((decision: USER_STATUS_POPUP_DECISION) => void) | null>(null);
    const [showMultipleInstancesConfirm, setShowMultipleInstancesConfirm] = useState(false);
    const multipleInstancesDecisionResolverRef = useRef<((decision: MULTIPLE_INSTANCES_CALL_DECISION) => void) | null>(
        null
    );
    const callEndedCallbacksRef = useRef<((call: Call) => void)[]>([]);
    const previousCallStatusRef = useRef<CallStatus>(CALL_STATUS.DISCONNECTED);
    const previousIsCallActiveRef = useRef<boolean>(false);
    const queryClient = useQueryClient();

    const [showAudioDeviceRequired, setShowAudioDeviceRequired] = useState(false);
    const [showAudioDeviceAutoSwitch, setShowAudioDeviceAutoSwitch] = useState(false);
    const [audioDeviceAutoSwitches, setAudioDeviceAutoSwitches] = useState<AudioDeviceAutoSwitch[]>([]);
    const pendingCallAfterDeviceRef = useRef<{
        phoneNumber: string;
        callData?: Omit<Partial<CallPayload>, "To" | "From">;
    } | null>(null);
    const pendingAutoSwitchResolvedRef = useRef<ResolvedAudioDevices | null>(null);
    const autoSwitchResolverRef = useRef<((confirmed: boolean) => void) | null>(null);
    const previousMediaDevicesRef = useRef<AudioDevice[] | null>(null);
    const { showDeviceChangeToasts } = useAudioDeviceChangeToasts();

    const { value: selectedBrand, onChange: setSelectedBrandConfig } = useRemoteConfigInput<BrandName>(
        SETTINGS_KEYS.SELECTED_BRAND
    );
    const { value: outgoingCallMelodyMuted } = useRemoteConfigInput<boolean>(SETTINGS_KEYS.OUTGOING_CALL_MELODY_MUTE);

    // Listen to changes in the outgoing call melody muted setting
    useEffect(() => {
        if (outgoingCallMelodyMuted !== undefined) {
            callManager.setOutgoingCallMelodyMuted(outgoingCallMelodyMuted);
        }
    }, [outgoingCallMelodyMuted]);

    const setSelectedBrand = (selectedBrand: BrandName) => {
        setSelectedBrandConfig(selectedBrand);
    };

    const { data: phoneNumbers } = useMyPhoneNumbers();
    const { t } = useTranslation();
    const { isMultipleInstances } = useSingleInstanceContext();

    const { userData } = useAuth();

    const { setIsBrandSelectorOpen } = useTopbar();

    const { data: currentRecordInformation } = useRecord(
        currentCallData?.recordId!,
        currentCallData?.moduleType!,
        selectedBrand
    );

    // Callback to handle call failures with notifications
    const handleCallFailure = useHandleCallFailure(t, userHungUpRef, {
        onActionableAudioIssue: () => {
            setShowAudioDeviceRequired(true);
        },
    });

    // Check if call is active
    const isCallActive = useCallback(
        (statuses?: CallStatus[]) => {
            const activeStatuses = statuses || [CALL_STATUS.CONNECTED, CALL_STATUS.CONNECTING, CALL_STATUS.RINGING];
            return activeStatuses.includes(callStatus);
        },
        [callStatus]
    );

    const onCallEnded = useCallback((callback: (call: Call) => void) => {
        callEndedCallbacksRef.current.push(callback);

        return () => {
            callEndedCallbacksRef.current = callEndedCallbacksRef.current.filter((cb) => cb !== callback);
        };
    }, []);

    useEffect(() => {
        if (
            callStatus === CALL_STATUS.DISCONNECTED &&
            previousCallStatusRef.current !== CALL_STATUS.DISCONNECTED &&
            currentCallData
        ) {
            callEndedCallbacksRef.current.forEach((callback) => {
                callback(currentCallData);
            });
            // Refresh the calls page list so the new/completed call appears
            queryClient.invalidateQueries({ queryKey: callKeys.lists() });
        }
        previousCallStatusRef.current = callStatus;
    }, [callStatus, currentCallData, queryClient]);

    // User status management for automatic IN_CALL state
    const { status: userStatus, updateStatus } = useUserStatusContext();
    const latestUserStatusRef = useRef<UserStatus | null>(null);
    const statusWaitersRef = useRef<Array<{ onStatus: (status: UserStatus) => boolean }>>([]);
    useEffect(() => {
        latestUserStatusRef.current = userStatus;
        if (userStatus) {
            // Notify one-shot listeners and keep only those still waiting
            statusWaitersRef.current = statusWaitersRef.current.filter((waiter) => !waiter.onStatus(userStatus));
        }
    }, [userStatus]);

    const ensureUserStatus = useCallback(async (targetStatus: UserStatus, timeoutMs = 4000) => {
        // If already in desired status, nothing to do
        if (latestUserStatusRef.current === targetStatus) return;

        await new Promise<void>((resolve, reject) => {
            const waiter = {
                onStatus: (status: UserStatus) => {
                    if (status === targetStatus) {
                        resolve();
                        return true; // remove this waiter
                    }
                    return false; // keep waiting
                },
            };

            // Register waiter
            statusWaitersRef.current.push(waiter);

            // Timeout cleanup and reject
            const timeoutId = setTimeout(() => {
                // Remove waiter if still present
                const idx = statusWaitersRef.current.indexOf(waiter);
                if (idx > -1) statusWaitersRef.current.splice(idx, 1);
                reject(new Error("Timed out waiting for status update"));
            }, timeoutMs);

            // If it resolves early, clear timeout
            const originalOnStatus = waiter.onStatus;
            waiter.onStatus = (status: UserStatus) => {
                const done = originalOnStatus(status);
                if (done) clearTimeout(timeoutId);
                return done;
            };
        });
    }, []);
    const previousStatusRef = useRef<UserStatus | null>(null);

    // Automatically change user status to IN_CALL when call becomes active
    // This will detect when the user is in a call and when they are not
    // and automatically change the user status to IN_CALL or restore the
    // previous status (the one that was before the call) when the call is ended
    useEffect(() => {
        const isCallActive =
            callStatus === CALL_STATUS.CONNECTED ||
            callStatus === CALL_STATUS.CONNECTING ||
            callStatus === CALL_STATUS.RINGING;

        const shouldEnterCall = isCallActive && userStatus !== UserStatus.IN_CALL;
        const shouldExitCall = !isCallActive && previousStatusRef.current && userStatus === UserStatus.IN_CALL;

        // Entering call
        if (shouldEnterCall) {
            previousStatusRef.current = userStatus;
            updateStatus(UserStatus.IN_CALL);
            return;
        }

        // Exiting call
        if (shouldExitCall) {
            const statusToRestore = previousStatusRef.current;
            if (statusToRestore) {
                previousStatusRef.current = null;
                // Restore to the original status before the call (ACTIVE, SILENT, etc.)
                // but never restore to INACTIVE after completing a call
                if (statusToRestore !== UserStatus.INACTIVE) {
                    updateStatus(statusToRestore);
                } else {
                    // If the original status was INACTIVE, set to ACTIVE instead
                    updateStatus(UserStatus.ACTIVE);
                }
            }
        }
    }, [callStatus, userStatus]);

    useEffect(() => {
        const isUserActive = userStatus === UserStatus.ACTIVE;
        const hasActiveCall =
            callStatus === CALL_STATUS.CONNECTED ||
            callStatus === CALL_STATUS.CONNECTING ||
            callStatus === CALL_STATUS.RINGING;

        if (isUserActive || hasActiveCall) {
            return;
        }

        void callManager.releaseMicrophoneAccess();
    }, [callStatus, userStatus]);

    // Start cooldown both when call becomes active and when call ends
    const [callButtonCooldown, setCallButtonCooldown] = useState(false);
    const hangupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Track previous call status to detect both call start and hangup
    const prevCallStatusRef = useRef<CallStatus>(callStatus);

    useEffect(() => {
        const prevStatus = prevCallStatusRef.current;
        const currentStatus = callStatus;

        // Start cooldown when call becomes active (connecting, ringing, connected)
        const wasInactive = !isCallActive([prevStatus]);
        const isNowActive = isCallActive([currentStatus]);

        // Start cooldown when call becomes active
        if (wasInactive && isNowActive) {
            setCallButtonCooldown(true);
            if (hangupTimeoutRef.current) clearTimeout(hangupTimeoutRef.current);
            hangupTimeoutRef.current = setTimeout(() => {
                setCallButtonCooldown(false);
            }, HANGUP_COOLDOWN);
        }

        // Start cooldown when call ends (was active, now disconnected)
        const wasActive = isCallActive([prevStatus]);
        const isNowDisconnected = currentStatus === CALL_STATUS.DISCONNECTED;

        if (wasActive && isNowDisconnected) {
            setCallButtonCooldown(true);
            if (hangupTimeoutRef.current) clearTimeout(hangupTimeoutRef.current);
            hangupTimeoutRef.current = setTimeout(() => {
                setCallButtonCooldown(false);
            }, HANGUP_COOLDOWN);
        }

        // Update previous status
        prevCallStatusRef.current = currentStatus;

        // Cleanup on unmount
        return () => {
            if (hangupTimeoutRef.current) clearTimeout(hangupTimeoutRef.current);
        };
    }, [callStatus, isCallActive]);

    // Update call duration every second when call is active
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (
            (callStatus === CALL_STATUS.CONNECTED ||
                callStatus === CALL_STATUS.CONNECTING ||
                callStatus === CALL_STATUS.RINGING) &&
            callStartTime
        ) {
            interval = setInterval(() => {
                const start = DateTime.fromISO(callStartTime).toMillis();
                const now = DateTime.now().toMillis();
                setCallDuration(Math.floor((now - start) / 1000));
            }, 1000);
        } else {
            setCallDuration(0);
        }
        return () => clearInterval(interval);
    }, [callStatus, callStartTime]);

    const initialize = useCallback(
        async (token: string) => {
            if (initialized || callManager.initialized) {
                setInitialized(true);
                return;
            }

            try {
                await callManager.initialize({
                    registrationToken: token,
                    outgoingCallMelodyMuted,
                });
                setInitialized(true);
            } catch (error) {
                console.error("Failed to initialize telephony:", error);
            }
        },
        [initialized, outgoingCallMelodyMuted]
    );

    useEffect(() => {
        if (!userData?.id || initialized || callManager.initialized) {
            return;
        }

        const registrationToken = getVoiceRegistrationToken(
            getLocalStorage<string>(LOCAL_STORAGE_KEYS.FCM_TOKEN.key, LOCAL_STORAGE_KEYS.FCM_TOKEN.default)
        );
        if (!registrationToken) {
            return;
        }

        void initialize(registrationToken);
    }, [userData?.id, initialized, initialize]);

    const applyResolvedAudioDevices = useCallback(async (resolved: ResolvedAudioDevices) => {
        await deviceManager.applyResolvedDevices(resolved);
        if (resolved.outputDeviceId && audioStateStore.getState().capabilities.supportsOutputRouting) {
            soundService.setOutputDevice(resolved.outputDeviceId);
        }
    }, []);

    const requestAudioDeviceAutoSwitchConfirmation = useCallback((resolved: ResolvedAudioDevices) => {
        return new Promise<boolean>((resolve) => {
            setAudioDeviceAutoSwitches(resolved.autoSwitches);
            pendingAutoSwitchResolvedRef.current = resolved;
            setShowAudioDeviceAutoSwitch(true);
            autoSwitchResolverRef.current = (confirmed: boolean) => {
                setShowAudioDeviceAutoSwitch(false);
                setAudioDeviceAutoSwitches([]);
                const pendingResolved = pendingAutoSwitchResolvedRef.current;
                pendingAutoSwitchResolvedRef.current = null;
                autoSwitchResolverRef.current = null;

                if (confirmed && pendingResolved) {
                    deviceManager.persistSelection(pendingResolved);
                }

                resolve(confirmed);
            };
        });
    }, []);

    type EnsureAudioDevicesForCallResult = "ready" | "incomplete" | "cancelled" | "error";

    const ensureAudioDevicesForCall = useCallback(async (): Promise<EnsureAudioDevicesForCallResult> => {
        const unnamedLabel = t("incident_audio_device_unnamed");

        try {
            const result = await deviceManager.resolveStoredDevices({
                persistResolved: false,
                unnamedLabel,
            });

            if (!result.isComplete) {
                return "incomplete";
            }

            if (result.autoSwitches.length > 0) {
                const confirmed = await requestAudioDeviceAutoSwitchConfirmation(result);
                if (!confirmed) {
                    return "cancelled";
                }
            } else {
                deviceManager.persistSelection(result);
            }

            await applyResolvedAudioDevices(result);
            return "ready";
        } catch (error) {
            console.error("Failed to resolve audio devices before call:", error);
            return "error";
        }
    }, [applyResolvedAudioDevices, requestAudioDeviceAutoSwitchConfirmation, t]);

    useEffect(() => {
        if (!initialized) {
            return;
        }

        let cancelled = false;
        const unnamedLabel = t("incident_audio_device_unnamed");

        const syncResolvedAudioDevices = async (notifyChanges: boolean) => {
            try {
                const result = await deviceManager.resolveStoredDevices({
                    persistResolved: true,
                    unnamedLabel,
                });

                if (cancelled) {
                    return;
                }

                if (notifyChanges) {
                    const diff = deviceManager.detectListDiff(previousMediaDevicesRef.current, result.devices);
                    showDeviceChangeToasts(diff, t);
                    logAudioDeviceListDiff(diff, { unnamedLabel });
                }

                // Auto-switches can happen on mount and on device changes, so log them regardless.
                logAudioDeviceAutoSwitches(result.autoSwitches, { unnamedLabel });

                previousMediaDevicesRef.current = result.devices;

                if (!result.isComplete) {
                    return;
                }

                await applyResolvedAudioDevices(result);
            } catch (error) {
                console.error("Failed to sync audio devices:", error);
            }
        };

        void syncResolvedAudioDevices(false);

        const unsubscribe = deviceManager.startDeviceChangeSync(() => {
            void syncResolvedAudioDevices(true);
        });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, [applyResolvedAudioDevices, initialized, showDeviceChangeToasts, t]);

    const setInputDevice = useCallback(
        async (deviceId: string) => {
            await deviceManager.setInputDevice(deviceId);
            const devices = previousMediaDevicesRef.current ?? [];
            const unnamedLabel = t("incident_audio_device_unnamed");
            const label =
                devices.find((device) => device.id === deviceId && device.kind === "audioinput")?.label ?? null;
            logAudioDeviceSelection("audioinput", deviceId, label, { unnamedLabel });
        },
        [t]
    );
    const setOutputDevice = useCallback(
        async (deviceId: string) => {
            await deviceManager.setOutputDevice(deviceId);
            const devices = previousMediaDevicesRef.current ?? [];
            const unnamedLabel = t("incident_audio_device_unnamed");
            const label =
                devices.find((device) => device.id === deviceId && device.kind === "audiooutput")?.label ?? null;
            logAudioDeviceSelection("audiooutput", deviceId, label, { unnamedLabel });
        },
        [t]
    );

    const appendToPhoneNumber = useCallback((digit: string) => {
        setCalledPhoneNumber((prev) => prev + digit);
    }, []);

    const getPhoneNumberAutomatically = useCallback(
        (calledNumber: string) => {
            return selectOutboundPhoneNumberAutomatically({
                calledNumber: calledNumber,
                phoneNumbers: phoneNumbers || [],
                selectedBrand,
                setIsBrandSelectorOpen,
                t,
            });
        },
        [phoneNumbers, selectedBrand]
    );

    const executeCall = useCallback(
        async (phoneNumber: string, callData?: Omit<Partial<CallPayload>, "To" | "From">) => {
            if (callStatus !== CALL_STATUS.DISCONNECTED) {
                console.warn("Call already active");
                return;
            }

            if (!phoneNumber) {
                toast.error(t("no_phone_number_to_call_provided"));
                console.error("No phone number to call provided");
                return;
            }

            if (!userData?.id) {
                toast.error(t("no_user_id_provided"));
                console.error("No user id provided");
                return;
            }

            let orgId = callData?.orgId || (selectedBrand ? BRANDS[selectedBrand].zohoOrganizationId : undefined);
            if (!orgId) {
                toast.error(t("no_brand_selected"));
                console.error("No org id provided");
                return;
            }

            // Final phone number to use for the call
            const autoNumber = getPhoneNumberAutomatically(phoneNumber);
            const fromNumber = callerPhoneNumber?.number || autoNumber;

            if (!fromNumber) {
                toast.error(t("no_phone_number_selected"));
                return;
            }

            setTemporaryCallerPhoneNumber(callerPhoneNumber?.number ? undefined : formatPhoneNumber(fromNumber));

            let formattedToNumber: string;
            let formattedFromNumber: string;
            try {
                formattedToNumber = formatPhoneNumber(phoneNumber, "E.164");
                formattedFromNumber = formatPhoneNumber(fromNumber, "E.164");
            } catch (error) {
                console.error("Stopping call because of invalid phone number", error);
                toast.error(t("invalid_phone_number"));
                return;
            }

            let callId: string | null = callData?.callId || null;
            if (!callId) {
                const call = await createCall({
                    To: formattedToNumber,
                    From: formattedFromNumber,
                    userId: userData?.id,
                    orgId,
                    moduleType: callData?.moduleType,
                    recordId: callData?.recordId,
                    appointmentId: callData?.appointmentId,
                    callPurpose: callData?.callPurpose,
                    calledName: callData?.calledName,
                    calledEmail: callData?.calledEmail,
                });
                callId = call.id;
                setCurrentCallData(call);
                console.log("Call created", call);
            } else {
                const call = await getCall(callId);
                setCurrentCallData(call);
                console.log("Call found", call);
            }

            try {
                setCalledPhoneNumber(formattedToNumber);
                setCallStartTime(DateTime.now().toISO());
                callManager.setStatusHandler(setCallStatus);
                callManager.setFailureHandler((failureType) => {
                    handleCallFailure(failureType, callId!);
                });
                await callManager.startCall({
                    To: formattedToNumber,
                    From: formattedFromNumber,
                    callId: callId,
                    userId: userData?.id,
                    orgId,
                    moduleType: callData?.moduleType,
                    recordId: callData?.recordId,
                });
            } catch (error) {
                setCallStatus(CALL_STATUS.DISCONNECTED);
                setCallStartTime(null);
                console.error("Failed to handle call:", error);
                toast.error(t("failed_to_handle_call"), {
                    description: t("failed_to_handle_call_description"),
                });
            }
        },
        [
            callStatus,
            callerPhoneNumber,
            userData?.id,
            getPhoneNumberAutomatically,
            setTemporaryCallerPhoneNumber,
            handleCallFailure,
        ]
    );

    const requestStatusDecisionFromIdle = useCallback(async () => {
        return await new Promise<USER_STATUS_POPUP_DECISION>((resolve, reject) => {
            // Open dialog and store resolver
            setShowIdleConfirm(true);
            idleDecisionResolverRef.current = (decision: USER_STATUS_POPUP_DECISION) => {
                // Close dialog first
                setShowIdleConfirm(false);
                if (decision === USER_STATUS_POPUP_DECISION.CANCEL) {
                    idleDecisionResolverRef.current = null;
                    reject(new Error("cancelled"));
                    return;
                }
                idleDecisionResolverRef.current = null;
                resolve(decision);
            };
        });
    }, []);

    const requestMultipleInstancesDecision = useCallback(async () => {
        return await new Promise<MULTIPLE_INSTANCES_CALL_DECISION>((resolve, reject) => {
            setShowMultipleInstancesConfirm(true);
            multipleInstancesDecisionResolverRef.current = (decision: MULTIPLE_INSTANCES_CALL_DECISION) => {
                setShowMultipleInstancesConfirm(false);

                if (decision === MULTIPLE_INSTANCES_CALL_DECISION.CANCEL) {
                    multipleInstancesDecisionResolverRef.current = null;
                    reject(new Error("cancelled"));
                    return;
                }

                multipleInstancesDecisionResolverRef.current = null;
                resolve(decision);
            };
        });
    }, []);

    const startCall = useCallback(
        async (phoneNumber: string, callData?: Omit<Partial<CallPayload>, "To" | "From">) => {
            // Prewarm the AudioContext synchronously while still inside the user gesture
            // handler. Chrome only allows AudioContext.resume() within a user gesture.
            // This must be the very first line — before any await.
            audioProcessingService.prewarm();

            if (!isMobile) {
                const micGranted = permissions[PermissionNames.MICROPHONE] === "granted";
                const notificationsGranted = permissions[PermissionNames.NOTIFICATIONS] === "granted";
                if (!micGranted || !notificationsGranted) {
                    openPermissionsModal();
                    return;
                }
            }

            if (callStatus !== CALL_STATUS.DISCONNECTED) {
                console.warn("Call already active");
                return;
            }

            const audioDevicesResult = await ensureAudioDevicesForCall();
            if (audioDevicesResult === "cancelled") {
                return;
            }
            if (audioDevicesResult === "incomplete" || audioDevicesResult === "error") {
                pendingCallAfterDeviceRef.current = { phoneNumber, callData };
                setShowAudioDeviceRequired(true);
                return;
            }

            if (isMultipleInstances) {
                try {
                    await requestMultipleInstancesDecision();
                } catch {
                    // User cancelled; stop execution
                    return;
                }
            }

            // Change selected brand based on orgId from call data if provided
            if (callData?.orgId) {
                const brandFromOrgId = getBrandFromOrgId(callData.orgId);
                if (brandFromOrgId) {
                    setSelectedBrand(brandFromOrgId);
                }
            }

            // If starting call from CRM while INACTIVE, automatically switch to ACTIVE (non-blocking backend)
            if (userStatus === UserStatus.INACTIVE && callData?.moduleType) {
                updateStatus(UserStatus.ACTIVE);
                await executeCall(phoneNumber, callData);
                return;
            }

            if (userStatus === UserStatus.IDLE) {
                try {
                    // Used for popup
                    const userStatusPopupDecision = await requestStatusDecisionFromIdle();
                    if (userStatusPopupDecision === USER_STATUS_POPUP_DECISION.ACTIVE) {
                        updateStatus(UserStatus.ACTIVE);
                        await ensureUserStatus(UserStatus.ACTIVE);
                        await executeCall(phoneNumber, callData);
                        return;
                    }

                    // Silent: set status and start the call
                    updateStatus(UserStatus.SILENT);
                    await ensureUserStatus(UserStatus.SILENT);
                    await executeCall(phoneNumber, callData);
                    return;
                } catch {
                    // User cancelled; stop execution
                    return;
                }
            }

            // Not idle: proceed as usual
            await executeCall(phoneNumber, callData);
        },
        [
            callStatus,
            userStatus,
            executeCall,
            requestStatusDecisionFromIdle,
            requestMultipleInstancesDecision,
            updateStatus,
            ensureUserStatus,
            isMultipleInstances,
            isMobile,
            permissions,
            openPermissionsModal,
            ensureAudioDevicesForCall,
        ]
    );

    // Button handlers for the decision dialog
    const handleCancelIdleDecision = useCallback(() => {
        idleDecisionResolverRef.current?.(USER_STATUS_POPUP_DECISION.CANCEL);
    }, []);

    const handleGoActive = useCallback(() => {
        idleDecisionResolverRef.current?.(USER_STATUS_POPUP_DECISION.ACTIVE);
    }, []);

    const handleGoSilent = useCallback(() => {
        idleDecisionResolverRef.current?.(USER_STATUS_POPUP_DECISION.SILENT);
    }, []);

    const handleCancelMultipleInstancesDecision = useCallback(() => {
        multipleInstancesDecisionResolverRef.current?.(MULTIPLE_INSTANCES_CALL_DECISION.CANCEL);
    }, []);

    const handleProceedDespiteMultipleInstances = useCallback(() => {
        multipleInstancesDecisionResolverRef.current?.(MULTIPLE_INSTANCES_CALL_DECISION.PROCEED);
    }, []);

    const handleAudioDeviceRequiredConfirm = useCallback(() => {
        const pending = pendingCallAfterDeviceRef.current;
        if (pending) {
            pendingCallAfterDeviceRef.current = null;
            void startCall(pending.phoneNumber, pending.callData);
        }
    }, [startCall]);

    const handleAudioDeviceRequiredCancel = useCallback(() => {
        pendingCallAfterDeviceRef.current = null;
    }, []);

    const handleAudioDeviceAutoSwitchConfirm = useCallback(() => {
        autoSwitchResolverRef.current?.(true);
    }, []);

    const handleAudioDeviceAutoSwitchCancel = useCallback(() => {
        autoSwitchResolverRef.current?.(false);
    }, []);

    const handleHangupCall = useCallback(() => {
        userHungUpRef.current = true;
        setCallStatus(CALL_STATUS.DISCONNECTED);
        setCallStartTime(null);
        void callManager.endCall();
    }, []);

    const handleSendDigits = useCallback((digits: string) => {
        callManager.sendDigits(digits);
    }, []);

    const handleMuteMic = useCallback(() => {
        if (!isCallActive()) {
            console.warn("Cannot mute/unmute: No active call");
            return;
        }

        // Get current mute status from Twilio
        const currentMuteStatus = callManager.isMuted();
        const newMuteStatus = !currentMuteStatus;

        console.log("Toggling mute:", { from: currentMuteStatus, to: newMuteStatus });

        // Apply mute/unmute to Twilio call first
        void callManager.toggleMute(newMuteStatus).then((success) => {
            if (!success) {
                console.error("Failed to apply mute/unmute to call");
                return;
            }

            setIsMuted(newMuteStatus);
            muteStatusSyncedRef.current = true;

            setTimeout(() => {
                const actualStatus = callManager.isMuted();
                if (actualStatus !== newMuteStatus) {
                    console.warn("Mute status verification failed:", {
                        expected: newMuteStatus,
                        actual: actualStatus,
                    });
                    setIsMuted(actualStatus);
                } else {
                    console.log("Mute status verified:", actualStatus);
                }
            }, 50);
        });
    }, [isCallActive]);

    // Sync mute status only when call starts or ends
    useEffect(() => {
        const currentlyActive = isCallActive();
        const wasActive = previousIsCallActiveRef.current;

        // Call state transition: inactive -> active
        if (currentlyActive && !wasActive) {
            // Call just started - sync initial mute status from Twilio
            const muted = callManager.isMuted();
            setIsMuted(muted);
            muteStatusSyncedRef.current = false; // Reset flag, allow sync
            console.log("Call started, initial mute status:", muted);
        }
        // Call state transition: active -> inactive
        else if (!currentlyActive && wasActive) {
            // Call just ended - reset everything
            setIsMuted(false);
            setAudioLevel([]);
            muteStatusSyncedRef.current = false;
            console.log("Call ended, reset mute status");
        }

        previousIsCallActiveRef.current = currentlyActive;
    }, [callStatus, isCallActive]);

    // Monitor audio level when call is active
    useEffect(() => {
        if (!isCallActive() || isMuted) {
            setAudioLevel([]);
            return;
        }

        // Start monitoring audio level
        callManager.startAudioLevelMonitoring((levels) => {
            setAudioLevel(levels);
        });

        return () => {
            callManager.stopAudioLevelMonitoring();
        };
    }, [isCallActive, isMuted]);

    // Use dev-only hook conditionally
    const devCallHelpers = useSimulateDevCall({
        callStatus,
        currentCallData,
        userData: userData || undefined,
        setCalledPhoneNumber,
        setCurrentCallData,
        setCallStartTime,
        setCallStatus,
    });

    return (
        <CallContext.Provider
            value={{
                initialized,
                setInitialized,
                temporaryCallerPhoneNumber,
                calledPhoneNumber,
                setCalledPhoneNumber,
                callerPhoneNumber,
                setCallerPhoneNumber,
                callStatus,
                setCallStatus,
                currentRecordInformation,
                currentCallData,
                callStartTime,
                setCallStartTime,
                callDuration,
                setInputDevice,
                setOutputDevice,
                appendToPhoneNumber,
                startCall,
                handleHangupCall,
                handleSendDigits,
                handleMuteMic,
                isMuted,
                audioLevel,
                simulateCallStart: devCallHelpers?.simulateCallStart,
                simulateCallEnd: devCallHelpers?.simulateCallEnd,
                initialize,
                isCallActive,
                selectedBrand,
                setSelectedBrand,

                callButtonCooldown,
                setCallButtonCooldown,

                onCallEnded,
            }}
        >
            {children}
            {/* Confirm dialog when trying to call with multiple instances open */}
            {/* AlertDialog is intentionally not dismissible via outside click or close button */}
            <AlertDialog open={showMultipleInstancesConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("multiple_instances")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("multiple_instances_call_warning_description", {
                                defaultValue:
                                    "Multiple instances of the app are open. It is recommended to close other tabs before placing a call.",
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleCancelMultipleInstancesDecision}>
                            {t("cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleProceedDespiteMultipleInstances}>
                            {t("call_anyway", { defaultValue: "Call anyway" })}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            {/* Confirm dialog when trying to call while IDLE */}
            {/* AlertDialog is intentionally not dismissible via outside click or close button */}
            <AlertDialog open={showIdleConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("cannot_make_calls_when_idle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("you_are_currently_idle_change_status_to_call", {
                                defaultValue:
                                    "You are currently idle. Please change your status to call or go silent to make a call.",
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
                        <AlertDialogAction
                            onClick={handleGoSilent}
                            className={`h-auto w-full whitespace-normal border bg-muted px-4 py-2 text-center text-foreground leading-snug hover:bg-muted/50 ${getStatusBorderColor(UserStatus.SILENT)}`}
                        >
                            {t("go_silent_and_call", { defaultValue: "Go Silent and Call" })}
                        </AlertDialogAction>
                        <AlertDialogAction
                            onClick={handleGoActive}
                            className={`h-auto w-full whitespace-normal border bg-muted px-4 py-2 text-center text-foreground leading-snug hover:bg-muted/50 ${getStatusBorderColor(UserStatus.ACTIVE)}`}
                        >
                            {t("go_active_and_call", { defaultValue: "Go Active and Call" })}
                        </AlertDialogAction>
                        <AlertDialogCancel
                            onClick={handleCancelIdleDecision}
                            className="mt-0 h-auto w-full whitespace-normal px-4 py-2 text-center leading-snug"
                        >
                            {t("cancel")}
                        </AlertDialogCancel>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <AudioDeviceAutoSwitchDialog
                open={showAudioDeviceAutoSwitch}
                autoSwitches={audioDeviceAutoSwitches}
                onOpenChange={setShowAudioDeviceAutoSwitch}
                onConfirm={handleAudioDeviceAutoSwitchConfirm}
                onCancel={handleAudioDeviceAutoSwitchCancel}
            />
            <AudioDeviceRequiredDialog
                open={showAudioDeviceRequired}
                onOpenChange={setShowAudioDeviceRequired}
                onConfirm={handleAudioDeviceRequiredConfirm}
                onCancel={handleAudioDeviceRequiredCancel}
            />
        </CallContext.Provider>
    );
}

export const useCall = (): CallContextType => {
    const context = useContext(CallContext);
    if (!context) {
        throw new Error("useCall must be used within a CallProvider");
    }
    return context;
};
